import React from "react";

/**
 * Sovereign Anchor — EIGOO's official fleet mark.
 *
 * A heraldic fouled anchor: crown ring, rope-wrapped shank, barbed crossbar,
 * and a pair of lifted flukes. Mustard-gold on dark green by default, but
 * every stroke is `currentColor` so the mark re-tints wherever it's placed.
 *
 * Props:
 *  - size        : number | string (defaults to 32; accepts "1em" etc.)
 *  - className   : extra classes (color via `text-*`)
 *  - strokeWidth : override line weight (default 6)
 *  - withGlow    : drop a subtle gold halo behind the mark (hero use)
 *  - title       : accessible label
 */
export default function SovereignAnchor({
    size = 32,
    className = "",
    strokeWidth = 6,
    withGlow = false,
    title = "EIGOO Sovereign Anchor",
    ...rest
}) {
    const dim = typeof size === "number" ? `${size}px` : size;
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 120 140"
            width={dim}
            height={dim}
            role="img"
            aria-label={title}
            className={className}
            {...rest}
        >
            <title>{title}</title>
            {withGlow && (
                <defs>
                    <radialGradient id="sa-glow" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="currentColor" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                    </radialGradient>
                </defs>
            )}
            {withGlow && <circle cx="60" cy="70" r="58" fill="url(#sa-glow)" />}

            <g
                fill="none"
                stroke="currentColor"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                {/* Crown ring (top shackle) */}
                <circle cx="60" cy="22" r="9" />
                {/* Shank — vertical spine */}
                <line x1="60" y1="31" x2="60" y2="110" />
                {/* Crossbar (stock) with upturned barbs */}
                <line x1="34" y1="48" x2="86" y2="48" />
                <line x1="34" y1="48" x2="30" y2="44" />
                <line x1="86" y1="48" x2="90" y2="44" />
                {/* Flukes — arc from the crown down around to the points */}
                <path d="M60 110 C 32 112, 18 96, 18 78" />
                <path d="M60 110 C 88 112, 102 96, 102 78" />
                {/* Inner fluke arms — bill points lifting up */}
                <path d="M18 78 L 30 84" />
                <path d="M102 78 L 90 84" />
                {/* Rope loop — the 'fouled' detail crossing the shank */}
                <path d="M44 72 C 52 64, 68 64, 76 72 C 82 78, 72 86, 64 82" strokeWidth={Math.max(3, strokeWidth - 2)} />
            </g>
        </svg>
    );
}
